/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { Periods, buildMomentInLocalTime } from 'cio/utils/metrics/common';
import {
  LAST_7_DAYS_RANGE_PRESET_UTC,
  LAST_30_DAYS_RANGE_PRESET_UTC,
  LAST_12_WEEKS_FROM_NOW_RANGE_PRESET_UTC,
  LAST_12_MONTHS_RANGE_PRESET,
} from 'cio/utils/workflow-metrics';
import { action, computed } from '@ember/object';
import moment from 'moment-timezone';
import { TimeRange } from 'cio/utils/time';

interface DashboardPageDatePickerArgs {
  timeRange: TimeRange | null;
  timePeriod: Periods | null;
  onRangeSelect: (range: TimeRange, presetValue: string) => void;
  disabled?: boolean;
}

export default class DashboardPageDatePicker extends Component<DashboardPageDatePickerArgs> {
  maxDate: moment.Moment;

  constructor(owner: unknown, args: DashboardPageDatePickerArgs) {
    super(owner, args);
    let now = moment.utc();
    this.maxDate = moment(`${now.format('YYYY-MM-DD')} 00:00:00`);
  }

  get disabled() {
    return this.args.disabled ?? false;
  }

  dateRangePresets = [
    LAST_7_DAYS_RANGE_PRESET_UTC,
    LAST_30_DAYS_RANGE_PRESET_UTC,
    LAST_12_WEEKS_FROM_NOW_RANGE_PRESET_UTC,
    LAST_12_MONTHS_RANGE_PRESET,
  ];

  @computed('args.timeRange.{start,end}', 'args.timePeriod')
  get timeRange() {
    if (this.args.timeRange) {
      return this.args.timeRange;
    }

    let preset = this.dateRangePresets.find((preset) => preset.value === this.args.timePeriod);

    if (!preset?.getRange) {
      return null;
    }

    return preset.getRange();
  }

  get timeRangeInLocalTimezone() {
    if (!this.timeRange?.start || !this.timeRange?.end) {
      return null;
    }

    return {
      start: buildMomentInLocalTime(this.timeRange.start).startOf('day'),
      end: buildMomentInLocalTime(this.timeRange.end).startOf('day'),
    };
  }

  @action
  onRangeSelect(value: any) {
    if (!value.date?.start || !value.date?.end) {
      return;
    }

    let { start, end } = value.date;

    let selectedPreset = this.dateRangePresets.find((preset) => {
      let presetRange = preset.getRange();

      if (!presetRange.start || !presetRange.end) {
        return false;
      }

      return presetRange.start.isSame(start) && presetRange.end.isSame(end);
    });

    if (selectedPreset) {
      let period = selectedPreset.value ?? Periods.CUSTOM;

      return this.args.onRangeSelect(selectedPreset.getRange(), period);
    } else {
      return this.args.onRangeSelect(value.date, Periods.CUSTOM);
    }
  }
}
